// Wake screen + keep-awake logic (no React here, so it is unit-testable).
//
// Every tenant, prod included, scales to zero when idle (.github/fleet/fleet.json, Aaron 2026-09-26), so an
// idle environment costs ~nothing but the first request after a quiet spell waits out a 30-90s backend boot.
// WakeGate shows a screen that says so, then offers "Keep awake for 1h / 2h / 4h", which asks the backend
// (/v6/public/keepwarm, app/keepwarm.py) to keep itself warm until then. Demo tenants get "demo" wording;
// prod tenants get the same explanation without it (Aaron 2026-09-27: "Offer it on prod too").
//
// "Cold" is the SERVER's word, not the browser's stopwatch. 2026-10-13 ~15:20Z, on site: g2's api ran
// min_instances 1 and served /health in 4-23 ms, yet the screen showed "Ready — Keep it awake — Continue (17)",
// because a far connection competing with the bundle took the browser past 3 s. A wait alone cannot tell a
// slow network from a boot, so /health reports `uptime_s` (aeronet-fast-api app/health.py) and the screen
// stays up only when that instance booted during the wait (bootedDuringWait). The "Starting up" notice still
// has to show before any answer arrives, so it waits FIRST_WAIT_MS — longer than a slow network on a warm
// backend usually takes, far shorter than a boot (30-90 s) — and closes silently if the answer says warm.
// Keep-awake is also offered on request from the Help menu (openKeepAwake), warm or not.
//
// Uses fetch, not axios: the axios interceptors add auth, retries and the DB-warming banner, none of
// which a pre-sign-in liveness probe wants.

export const FIRST_WAIT_MS = 8000; // answer inside this and the screen never shows (3 s tripped on site, warm)
export const POLL_TIMEOUT_MS = 15000;
export const POLL_GAP_MS = 2000;
export const READY_AUTO_CONTINUE_S = 20;
export const KEEP_AWAKE_HOURS = [1, 2, 4] as const;
/** An instance up for less than the wait plus this booted for us (the prewarm may start it during sign-in). */
export const FRESH_INSTANCE_S = 180;
/** window event that opens the keep-awake dialog (WakeGate listens; the Help menu fires it). */
export const KEEP_AWAKE_EVENT = "aeronet:keep-awake";

/**
 * Demo tenant or paying one: decides the wording only. The tenant's own `demo` flag (src/config/product.ts)
 * wins; without one, the .aeronet.dev zone is demo and .aeronet.app is prod. Mustang is the case the suffix
 * gets wrong: a demo tenant on the .app zone.
 */
export function isDemoTenant(hostname: string, configDemo?: boolean): boolean {
  return configDemo ?? hostname.endsWith(".aeronet.dev");
}

/** No gate on local dev (relative API base) or in tests. */
export function shouldSkip(apiBase: string, hostname: string, nodeEnv?: string): boolean {
  if (nodeEnv === "test") return true;
  if (!apiBase) return true;
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname.endsWith(".localhost");
}

/**
 * Fire-and-forget GET <api>/health at page load, so a cold backend boots while the user signs in. Shows
 * nothing: the wake screen itself waits for sign-in (WakeGate `signedIn`). keepalive lets it outlive the
 * Cognito hosted-UI redirect.
 */
export function prewarm(apiBase: string, fetchImpl: typeof fetch = fetch): void {
  if (!apiBase) return;
  fetchImpl(`${apiBase}/health`, { cache: "no-store", keepalive: true }).catch(() => {});
}

/** What /health said: the answering instance's age, when its image reports one. */
export type Health = { uptimeS?: number };

/**
 * GET <api>/health. Answered once the backend ANSWERS: 200, or 404 from an image that predates /health.
 * A 5xx (the load balancer while the container boots), a timeout or a network error means still waking (null).
 */
export async function probe(apiBase: string, timeoutMs: number, fetchImpl: typeof fetch = fetch): Promise<Health | null> {
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), timeoutMs);
  try {
    const r = await fetchImpl(`${apiBase}/health`, { signal: ctl.signal, cache: "no-store" });
    if (r.status !== 200 && r.status !== 404) return null;
    const body = r.status === 200 ? await r.json().catch(() => ({})) : {};
    return typeof body?.uptime_s === "number" ? { uptimeS: body.uptime_s } : {};
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Did the answering instance boot during this wait? Only the server knows: an instance up longer than the wait
 * (plus FRESH_INSTANCE_S for a boot the page-load prewarm started during sign-in) was already warm, and the wait
 * was the network. An image that predates uptime_s cannot say, so the browser's own timing stands.
 */
export function bootedDuringWait(health: Health, waitedMs: number): boolean {
  if (health.uptimeS === undefined) return true;
  return health.uptimeS <= waitedMs / 1000 + FRESH_INSTANCE_S;
}

/** Open the keep-awake dialog on request, warm or not (the Help menu's "Keep awake…"). */
export function openKeepAwake(): void {
  window.dispatchEvent(new Event(KEEP_AWAKE_EVENT));
}

export type KeepWarmStatus = { enabled: boolean; until?: string | null; maxHours?: number; message?: string };

export async function keepWarmStatus(apiBase: string, fetchImpl: typeof fetch = fetch): Promise<KeepWarmStatus> {
  try {
    const r = await fetchImpl(`${apiBase}/v6/public/keepwarm`, { cache: "no-store" });
    if (r.status === 503) return { enabled: true, message: (await r.json()).message };
    if (!r.ok) return { enabled: false };
    const j = await r.json();
    return { enabled: !!j.enabled, until: j.until ?? null, maxHours: j.max_hours };
  } catch {
    return { enabled: false };
  }
}

const offered = new Map<string, Promise<boolean>>();

/**
 * Does this tenant offer keep-awake? Asked at most once per page (the status call lists Cloud Tasks), and only
 * by the Help menu when it opens. False on local dev and on a backend without the route (404).
 */
export function keepAwakeAvailable(apiBase: string, fetchImpl: typeof fetch = fetch): Promise<boolean> {
  if (!apiBase) return Promise.resolve(false);
  if (!offered.has(apiBase)) offered.set(apiBase, keepWarmStatus(apiBase, fetchImpl).then((st) => st.enabled));
  return offered.get(apiBase)!;
}

export async function keepAwake(apiBase: string, hours: number, fetchImpl: typeof fetch = fetch): Promise<KeepWarmStatus> {
  try {
    const r = await fetchImpl(`${apiBase}/v6/public/keepwarm`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ hours }),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) return { enabled: r.status !== 404, message: j.message || `Keep-awake failed (HTTP ${r.status}).` };
    return { enabled: true, until: j.until ?? null };
  } catch {
    return { enabled: true, message: "Keep-awake request did not reach the server." };
  }
}

/** "3:40 pm" in the viewer's locale, or "" for a missing/invalid time. */
export function formatUntil(iso?: string | null, locale?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" });
}
